import isEmpty from 'lodash/isEmpty'
import toNumber from 'lodash/toNumber'
import { toDate, type DateType } from '@ztechapps/common.utils'
import { HaulerFieldComparisonOpEnum } from '../enums/HaulerFieldComparisonOpEnum'
import { HaulerRequiredWhenCombinatorEnum } from '../enums/HaulerRequiredWhenCombinatorEnum'
import { HaulerRequiredWhenOpEnum } from '../enums/HaulerRequiredWhenOpEnum'
import type { RequiredWhenComparison, RequiredWhenTrigger } from './FieldValidation'
import type { HaulerTemplateFieldDefinition } from './HaulerTemplateDefinition'

// Mirrored in the mobile app's apps/totalmilk/hauler-mobile/src/template/evaluateFieldRules.ts — Metro can't import this package, so mobile keeps its own copy.

/** Milliseconds in a day, used to convert an elapsed-ms `diff()` into a day count. */
const MS_PER_DAY = 86_400_000

/** Returns true when a value is absent or blank (null, undefined, or empty string). */
function isBlank(v: unknown): boolean {
  return v === null || v === undefined || v === ''
}

/**
 * Converts a camelCase field key to a human-readable label.
 * Example: 'pickupTareWeight' → 'Pickup Tare Weight'
 */
function toLabel(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^(.)/, (c) => c.toUpperCase())
}

/** Human-readable phrase for each `requiredWhen` operator. */
const OP_PHRASE: Record<HaulerRequiredWhenOpEnum, string> = {
  [HaulerRequiredWhenOpEnum.Gt]: 'is greater than',
  [HaulerRequiredWhenOpEnum.Gte]: 'is at least',
  [HaulerRequiredWhenOpEnum.Lt]: 'is less than',
  [HaulerRequiredWhenOpEnum.Lte]: 'is at most',
  [HaulerRequiredWhenOpEnum.Equals]: 'is',
  [HaulerRequiredWhenOpEnum.Contains]: 'contains',
  [HaulerRequiredWhenOpEnum.IsEmpty]: 'is empty',
  [HaulerRequiredWhenOpEnum.IsNotEmpty]: 'is not empty',
}

/** Evaluates one comparison against a trigger's source-field value. */
function evaluateComparison(sibling: unknown, comparison: RequiredWhenComparison): boolean {
  const { op, value } = comparison
  switch (op) {
    case HaulerRequiredWhenOpEnum.Gt:
      return toNumber(sibling) > toNumber(value)
    case HaulerRequiredWhenOpEnum.Lt:
      return toNumber(sibling) < toNumber(value)
    case HaulerRequiredWhenOpEnum.Gte:
      return toNumber(sibling) >= toNumber(value)
    case HaulerRequiredWhenOpEnum.Lte:
      return toNumber(sibling) <= toNumber(value)
    case HaulerRequiredWhenOpEnum.Equals:
      return value != null && String(sibling ?? '') === String(value)
    case HaulerRequiredWhenOpEnum.Contains:
      return value != null && String(sibling ?? '').includes(String(value))
    case HaulerRequiredWhenOpEnum.IsEmpty:
      return isBlank(sibling)
    case HaulerRequiredWhenOpEnum.IsNotEmpty:
      return !isBlank(sibling)
    default:
      return false
  }
}

/** Builds the message phrase for a single comparison. Empty operators carry no value. */
function comparisonPhrase(comparison: RequiredWhenComparison): string {
  const phrase = OP_PHRASE[comparison.op]
  if (comparison.op === HaulerRequiredWhenOpEnum.IsEmpty || comparison.op === HaulerRequiredWhenOpEnum.IsNotEmpty) {
    return phrase
  }
  return `${phrase} ${comparison.value}`
}

/** Old `FieldValidation.requiredWhen` numeric-comparison operator (legacy: `op` only ever held one of these 4 relational `HaulerFieldComparisonOpEnum` members, reused here to type the legacy shape). */
type LegacyRequiredWhenOp =
  | HaulerFieldComparisonOpEnum.Gte
  | HaulerFieldComparisonOpEnum.Lte
  | HaulerFieldComparisonOpEnum.Gt
  | HaulerFieldComparisonOpEnum.Lt

/** Legacy shape persisted for `FieldValidation.requiredWhen` (older templates, before requiredWhen became `RequiredWhenTrigger[]`). */
type LegacyRequiredWhen = {
  field: string
  equals?: unknown
  contains?: string
  op?: LegacyRequiredWhenOp
  value?: number
}

/** Maps the old relational `op` to its `HaulerRequiredWhenOpEnum` equivalent — the two enums use identical wire slugs ('gt'/'lt'/'gte'/'lte') but are distinct types. */
const LEGACY_OP_MAP: Record<LegacyRequiredWhenOp, HaulerRequiredWhenOpEnum> = {
  [HaulerFieldComparisonOpEnum.Gte]: HaulerRequiredWhenOpEnum.Gte,
  [HaulerFieldComparisonOpEnum.Lte]: HaulerRequiredWhenOpEnum.Lte,
  [HaulerFieldComparisonOpEnum.Gt]: HaulerRequiredWhenOpEnum.Gt,
  [HaulerFieldComparisonOpEnum.Lt]: HaulerRequiredWhenOpEnum.Lt,
}

/**
 * Builds the single comparison for a legacy `requiredWhen` object, using its old precedence:
 * `equals` first, then `contains`, then the numeric `op`/`value` pair. Returns `undefined` for a
 * malformed object matching none of the three.
 */
function buildLegacyComparison(legacy: LegacyRequiredWhen): RequiredWhenComparison | undefined {
  if (legacy.equals != null) return { op: HaulerRequiredWhenOpEnum.Equals, value: String(legacy.equals) }
  if (legacy.contains != null) return { op: HaulerRequiredWhenOpEnum.Contains, value: legacy.contains }
  if (legacy.op != null) return { op: LEGACY_OP_MAP[legacy.op], value: legacy.value }
  return undefined
}

/**
 * Normalizes a persisted `requiredWhen` value into the current `RequiredWhenTrigger[]` shape.
 *
 * Legacy templates (older persisted templates) persisted `requiredWhen` as a single object
 * (`{ field, equals?, contains?, op?, value? }`) instead of a trigger array, so old data read
 * back from Mongo can still hold that shape. This converts it into a one-trigger array — never
 * drops it — using the old precedence (`equals` → `contains` → numeric `op`/`value`); new-shape
 * data (already an array) passes through unchanged; nullish/empty input returns `[]`.
 */
export function normalizeRequiredWhen(input: unknown): RequiredWhenTrigger[] {
  if (isEmpty(input)) return []
  if (Array.isArray(input)) return input as RequiredWhenTrigger[]

  const legacy = input as LegacyRequiredWhen
  const comparison = buildLegacyComparison(legacy)
  if (comparison == null) return []

  return [
    {
      field: legacy.field,
      combinator: HaulerRequiredWhenCombinatorEnum.And,
      comparisons: [comparison],
    },
  ]
}

/**
 * Evaluates the `validation` rules for a single field and returns the first
 * error message found, or `undefined` when the value is valid.
 *
 * Empty-value skip: when `value` is null, undefined, or empty string, all rules
 * except `requiredWhen` are skipped. This function does NOT decide whether a blank
 * value is itself an error for a required field — that is handled separately by the
 * existing required check. Range/length rules apply to any
 * non-blank value regardless of the field's `required` flag.
 *
 * Rule evaluation order: requiredWhen (any trigger satisfied) → the empty-value
 * skip → min → max → minLength → maxLength.
 *
 * @param value            - The current raw value of the field.
 * @param allValues        - A snapshot of every field's current value in the form.
 * @param field            - The field's `HaulerTemplateFieldDefinition`, supplying its `required`
 *                           flag, `label` and `validation` rules.
 * @param getLabelForKey   - Optional resolver that maps a sibling field key to its display label.
 *                           Used to build descriptive `requiredWhen` messages. Falls back to the
 *                           raw key when omitted or when the key is not found.
 * @returns The first error message string, or `undefined` if no rule failed.
 */
export function evaluateFieldRules(
  value: unknown,
  allValues: Record<string, unknown>,
  field: HaulerTemplateFieldDefinition,
  getLabelForKey?: (key: string) => string
): string | undefined {
  const { validation, label: fieldLabel } = field
  if (validation == null) return undefined

  // --- requiredWhen (evaluated even when value is empty, only when the field's Required flag is on) ---
  // The Required flag is the master switch: off means never required, requiredWhen rules included.
  // The triggers are OR-combined: the field is required as soon as ANY trigger fires.
  // Within one trigger the comparisons are combined by its combinator (And → every, Or → some).
  // normalizeRequiredWhen guards this loop against legacy persisted data (older templates), which
  // stored a single { field, equals?, contains?, op?, value? } object instead of a trigger array.
  if (field.required) {
    const triggers = normalizeRequiredWhen(validation.requiredWhen)
    for (const trigger of triggers) {
      const siblingValue = allValues[trigger.field]
      const results = trigger.comparisons.map((comparison) => evaluateComparison(siblingValue, comparison))
      const triggerSatisfied =
        trigger.combinator === HaulerRequiredWhenCombinatorEnum.And ? results.every(Boolean) : results.some(Boolean)

      if (triggerSatisfied) {
        if (isBlank(value)) {
          // Message describes the FIRST satisfied trigger; its comparison phrases are
          // joined by the combinator slug ('and'/'or'), which doubles as the display word.
          const otherFieldLabel = getLabelForKey?.(trigger.field) ?? trigger.field
          const phrase = trigger.comparisons.map(comparisonPhrase).join(` ${trigger.combinator} `)
          return `Required if ${otherFieldLabel} ${phrase}`
        }
        break
      }
    }
  }

  // All remaining rules are skipped when the value is empty
  if (isBlank(value)) return undefined

  // --- min ---
  if (validation.min != null) {
    if (toNumber(value) < validation.min) {
      return `${fieldLabel} must be at least ${validation.min}`
    }
  }

  // --- max ---
  if (validation.max != null) {
    if (toNumber(value) > validation.max) {
      return `${fieldLabel} must be at most ${validation.max}`
    }
  }

  // --- minLength ---
  if (validation.minLength != null) {
    if (String(value).length < validation.minLength) {
      return `${fieldLabel} must be at least ${validation.minLength} characters`
    }
  }

  // --- maxLength ---
  if (validation.maxLength != null) {
    if (String(value).length > validation.maxLength) {
      return `${fieldLabel} must be at most ${validation.maxLength} characters`
    }
  }

  // --- dateWindow ---
  // Mirrored in the mobile app's apps/totalmilk/hauler-mobile/src/template/evaluateFieldRules.ts
  // (dateWindow branch) — Metro can't import this package, so that copy is hand-duplicated;
  // keep it in sync if this branch changes.
  if (validation.dateWindow != null) {
    const { other, daysBefore, daysAfter } = validation.dateWindow
    const otherValue = allValues[other]

    if (!isBlank(otherValue)) {
      // Elapsed-duration (tz-invariant) day count — a duration between two instants, matching
      // the pre-#1458 hardcoded check. Deliberately NOT moment's calendar-aware diff('days', true),
      // which is DST/tz-dependent and would let a device-tz client and host-tz backend disagree
      // at a DST boundary. `.diff()` with no unit returns milliseconds, so this needs no
      // `.duration` (which the common toDate Moment doesn't expose).
      const days = toDate(value as DateType).diff(toDate(otherValue as DateType)) / MS_PER_DAY
      const otherLabel = getLabelForKey?.(other) ?? toLabel(other)

      if (daysAfter != null && days > daysAfter) {
        return `${fieldLabel} must be within ${daysAfter} day(s) after ${otherLabel}`
      }
      if (daysBefore != null && days < -daysBefore) {
        return `${fieldLabel} must be within ${daysBefore} day(s) before ${otherLabel}`
      }
    }
  }

  return undefined
}
